import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Badge,
  Button,
  Dropdown,
  DropdownMenu,
  DropdownToggle,
  FormGroup,
  Input,
  Label,
} from "reactstrap";

import type { OrderPaymentMethod } from "../../../../services/orderService";
import type { User as StudioUser } from "../../../../services/userService";

export type OrdersPaymentMethodFilter = "" | OrderPaymentMethod;
export type OrdersSortFilter = "id_desc" | "discount_desc" | "discount_asc";

type OrdersFiltersPanelProps = {
  paymentMethodFilter: OrdersPaymentMethodFilter;
  paymentMethodOptions: OrderPaymentMethod[];
  loadingPaymentMethods: boolean;
  hasDiscountFilter: boolean;
  appliedByUserId: number | "";
  canFilterByApplier: boolean;
  teamUsers: StudioUser[];
  sortFilter: OrdersSortFilter;
  onPaymentMethodChange: (value: OrdersPaymentMethodFilter) => void;
  onHasDiscountChange: (checked: boolean) => void;
  onAppliedByChange: (value: number | "") => void;
  onSortChange: (value: OrdersSortFilter) => void;
  onClearFilters: () => void;
};

const OrdersFiltersPanel = ({
  paymentMethodFilter,
  paymentMethodOptions,
  loadingPaymentMethods,
  hasDiscountFilter,
  appliedByUserId,
  canFilterByApplier,
  teamUsers,
  sortFilter,
  onPaymentMethodChange,
  onHasDiscountChange,
  onAppliedByChange,
  onSortChange,
  onClearFilters,
}: OrdersFiltersPanelProps) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const hasActiveFilters =
    Boolean(paymentMethodFilter) ||
    hasDiscountFilter ||
    appliedByUserId !== "" ||
    sortFilter !== "id_desc";

  const activeBadges: string[] = [];

  if (paymentMethodFilter) {
    activeBadges.push(t(`studio.orders.checkout.payment.method.${paymentMethodFilter}`));
  }

  if (hasDiscountFilter) {
    activeBadges.push(t("studio.orders.filters.courtesyOnlyShort"));
  }

  if (appliedByUserId !== "") {
    const applier = teamUsers.find((user) => user.id === appliedByUserId);
    activeBadges.push(
      applier?.name ?? t("studio.orders.filters.appliedByShort")
    );
  }

  if (sortFilter === "discount_desc") {
    activeBadges.push(t("studio.orders.filters.sortDiscountDesc"));
  } else if (sortFilter === "discount_asc") {
    activeBadges.push(t("studio.orders.filters.sortDiscountAsc"));
  }

  return (
    <div className="orders-filters-panel d-flex flex-wrap align-items-center gap-2">
      {hasActiveFilters ? (
        <div className="orders-filters-panel__badges d-flex flex-wrap align-items-center gap-1">
          {activeBadges.map((label) => (
            <Badge
              key={label}
              color="light"
              className="text-body border border-secondary-subtle fw-normal"
            >
              {label}
            </Badge>
          ))}
          <Button
            color="link"
            size="sm"
            className="px-1 py-0"
            type="button"
            onClick={onClearFilters}
          >
            {t("studio.orders.filters.clear")}
          </Button>
        </div>
      ) : null}
      <Dropdown
        isOpen={open}
        toggle={() => setOpen((current) => !current)}
        className="orders-filters-dropdown"
      >
        <DropdownToggle
          color={hasActiveFilters ? "success" : "secondary"}
          outline
          size="sm"
          className={
            hasActiveFilters
              ? "orders-filters-dropdown__toggle orders-filters-dropdown__toggle--active"
              : "orders-filters-dropdown__toggle bg-white text-body border-secondary-subtle"
          }
          caret
        >
          <i className="ri-filter-3-line align-middle me-1" aria-hidden="true" />
          {t("studio.orders.filters.panelLabel")}
          {hasActiveFilters ? (
            <Badge
              color="success"
              pill
              className="orders-filters-dropdown__count ms-1 align-middle"
            >
              {activeBadges.length}
            </Badge>
          ) : null}
        </DropdownToggle>
        <DropdownMenu
          end
          container="body"
          positionFixed
          className="orders-filters-dropdown__menu p-3"
          style={{ minWidth: "18rem" }}
          onClick={(event: React.MouseEvent) => event.stopPropagation()}
        >
          <FormGroup className="mb-3">
            <Label for="orders-filter-payment" className="small fw-medium">
              {t("studio.orders.filters.paymentMethodLabel")}
            </Label>
            <Input
              id="orders-filter-payment"
              type="select"
              bsSize="sm"
              value={paymentMethodFilter}
              disabled={loadingPaymentMethods}
              onChange={(event) =>
                onPaymentMethodChange(event.target.value as OrdersPaymentMethodFilter)
              }
            >
              <option value="">
                {loadingPaymentMethods
                  ? t("studio.orders.filters.loadingPaymentMethods")
                  : t("studio.orders.filters.allPaymentMethods")}
              </option>
              {paymentMethodOptions.map((method) => (
                <option key={method} value={method}>
                  {t(`studio.orders.checkout.payment.method.${method}`)}
                </option>
              ))}
            </Input>
          </FormGroup>

          <FormGroup check className="mb-3">
            <Input
              id="orders-filter-courtesy"
              type="checkbox"
              checked={hasDiscountFilter}
              onChange={(event) => onHasDiscountChange(event.target.checked)}
            />
            <Label check htmlFor="orders-filter-courtesy" className="ms-1">
              {t("studio.orders.filters.courtesyOnly")}
            </Label>
          </FormGroup>

          {hasDiscountFilter && canFilterByApplier && teamUsers.length > 0 ? (
            <FormGroup className="mb-3">
              <Label for="orders-filter-applier" className="small fw-medium">
                {t("studio.orders.filters.appliedByLabel")}
              </Label>
              <Input
                id="orders-filter-applier"
                type="select"
                bsSize="sm"
                value={appliedByUserId === "" ? "" : String(appliedByUserId)}
                onChange={(event) => {
                  const value = event.target.value;
                  onAppliedByChange(value ? Number(value) : "");
                }}
              >
                <option value="">{t("studio.orders.filters.allAppliers")}</option>
                {teamUsers.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.name}
                  </option>
                ))}
              </Input>
            </FormGroup>
          ) : null}

          <FormGroup className="mb-0">
            <Label for="orders-filter-sort" className="small fw-medium">
              {t("studio.orders.filters.sortLabel")}
            </Label>
            <Input
              id="orders-filter-sort"
              type="select"
              bsSize="sm"
              value={sortFilter}
              onChange={(event) =>
                onSortChange(event.target.value as OrdersSortFilter)
              }
            >
              <option value="id_desc">{t("studio.orders.filters.sortNewest")}</option>
              <option value="discount_desc">
                {t("studio.orders.filters.sortDiscountDesc")}
              </option>
              <option value="discount_asc">
                {t("studio.orders.filters.sortDiscountAsc")}
              </option>
            </Input>
          </FormGroup>
        </DropdownMenu>
      </Dropdown>
    </div>
  );
};

export default OrdersFiltersPanel;
